import React from 'react';
import { useWishlist } from '../context/WishlistContext';
import { useProducts } from '../hooks/useProducts';
import ProductCard from '../components/ProductCard';
import { useAuth } from '../context/AuthContext';
import { Heart, ShoppingBag, ArrowLeft } from 'lucide-react';
import { Link } from 'wouter';

export default function Wishlist() {
  const { wishlistIds } = useWishlist();
  const { products, loading } = useProducts();
  const { user, signInWithGoogle } = useAuth();

  const handleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error: any) {
      if (error.code === 'auth/popup-closed-by-user') return;
      console.error(error);
    }
  };

  const wishlistProducts = products.filter((p) => wishlistIds.includes(p.id));

  if (loading) {
    return <div className="min-h-screen bg-spice-light flex items-center justify-center text-white font-bold tracking-widest uppercase text-xs">Loading Wishlist...</div>;
  }

  return (
    <div className="min-h-screen bg-spice-light pt-8 pb-32 px-3 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <button 
          onClick={() => window.history.back()}
          className="mb-6 flex items-center gap-2 text-[#1B120C] hover:text-spice-accent transition-colors font-bold uppercase tracking-widest text-[10px] group min-h-[44px] min-w-[44px]"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
          Back
        </button>
        {!user && (
          <div className="mb-6 p-4 sm:p-6 bg-[#FAF5EB] border border-[rgba(27,18,12,0.1)] rounded-2xl shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-[#63574E] text-xs sm:text-sm font-medium italic text-center sm:text-left">Login to sync your favorite spices across all your devices.</p>
            <button
              onClick={handleSignIn}
              className="bg-spice-accent text-white px-6 py-2.5 rounded-xl font-bold uppercase tracking-widest text-[10px] hover:bg-spice-red active:scale-95 transition-all whitespace-nowrap min-h-[44px]"
            >
              Sign In with Google
            </button>
          </div>
        )}
        <div className="border-b border-[rgba(27,18,12,0.1)] pb-8 mb-8 sm:mb-12 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl md:text-6xl font-bold text-[#1B120C] mb-2 sm:mb-4">My Wishlist</h1>
            <p className="text-[#63574E] max-w-xl text-sm sm:text-lg leading-relaxed italic">
              Saved items you're keeping an eye on. Add them to your bag when you're ready to spice up your kitchen.
            </p>
          </div>
          <div className="bg-[#FAF5EB] px-5 py-3.5 border border-[rgba(27,18,12,0.1)] rounded-2xl shadow-sm flex items-center gap-4 self-start md:self-auto">
            <div className="w-10 h-10 bg-spice-accent/10 rounded-xl flex items-center justify-center">
              <Heart className="w-5 h-5 text-spice-accent fill-current" />
            </div>
            <div>
              <div className="text-[9px] font-bold uppercase tracking-widest text-[#63574E]">Saved Items</div>
              <div className="text-xl sm:text-2xl font-black text-[#1B120C]">{wishlistIds.length}</div>
            </div>
          </div>
        </div>

        {wishlistProducts.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 border-l border-t border-[#E5E0D8] bg-white">
            {wishlistProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <div className="text-center py-24 bg-white/5 border border-dashed border-white/10 rounded-3xl">
            <div className="w-20 h-20 bg-[#1C1F26] rounded-2xl flex items-center justify-center mx-auto mb-6 border border-white/5">
              <ShoppingBag className="w-10 h-10 text-white/20" />
            </div>
            <h3 className="font-serif text-2xl font-bold text-white mb-2">Your wishlist is empty</h3>
            <p className="text-white/40 mb-8 font-medium">Browse our collection and save your favorite spices!</p>
            <Link 
              href="/products"
              className="inline-block bg-spice-accent text-white px-10 py-4 rounded-xl font-bold uppercase tracking-widest text-[10px] hover:scale-105 transition-all shadow-xl shadow-spice-accent/20"
            >
              Browse Products
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
